import {
  afterNextRender,
  computed,
  Directive,
  effect,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Injector,
  input,
  linkedSignal,
  model,
  Output,
  Signal,
  signal,
  untracked,
  ViewChild,
  ViewContainerRef,
  WritableSignal,
} from '@angular/core';
import { UiControlDirective } from './ui-control.directive';
import {
  IUiInputSettings,
  LOGGING_SERVICE_TOKEN,
  PERMISSIONS,
  StateEnum,
  TRegisteredValidation,
  TValidators,
  UiInputSettings,
  UTILS,
} from '@aksia/infrastructure';

@Directive({
  selector: 'uiinput',
  standalone: true,
})
export class UiInputDirective<T = unknown> extends UiControlDirective {
  //#region Host Bindings

  @HostBinding('attr.uilog') get uilog() {
    return this.tag() ?? 'untagged';
  }

  //#endregion

  //#region Injections

  protected readonly injector = inject(Injector);

  //#endregion

  //#region Inputs/Ouputs

  override settings = input<UiInputSettings, IUiInputSettings>(
    new UiInputSettings(),
    {
      transform: (settings: IUiInputSettings) => {
        return this.initSettings(settings);
      },
    },
  );

  value = model<T | undefined>();
  initialValue = input(undefined, {
    transform: (value: T | undefined) => this.setInitialValue(value),
  });

  validators = input([], {
    transform: (validators: TValidators) => {
      let validatorsFn =
        typeof validators === 'function' ? validators() : validators;
      return validatorsFn;
    },
  });

  forceValidation = input<unknown | undefined>(undefined);

  validationChain = input<Signal<Array<string>> | Array<string>>([], {
    // eslint-disable-next-line @angular-eslint/no-input-rename -- established v1 public binding name; a dotted name cannot be a class property, so the alias is unavoidable
    alias: 'validation.chain',
  });

  labelCssClass = input<string | undefined>();
  inputCssClass = input<string | undefined>();
  toolstripCssClass = input<string | undefined>();
  footerContent = input<string | undefined>();

  /**
   * @deprecated Use valueChange instead
   */
  @Output() ValueChanged: EventEmitter<unknown> = new EventEmitter<unknown>();

  //#endregion

  //#region Host Bindings

  @HostBinding('attr.uiinput')
  uiInput = '';

  @HostBinding('attr.uistate') override get uiState() {
    return this.state();
  }

  //#endregion

  //#region View Children

  @ViewChild('uiValueRef') uiValueRef!: ElementRef;
  @ViewChild('uiToolsMarkupRef') uiToolRef!: ElementRef;
  @ViewChild('uiToolsCodeRef', { read: ViewContainerRef })
  uiToolCodeRef!: ViewContainerRef;
  @ViewChild('uiModalsCodeRef', { read: ViewContainerRef })
  uiModalsCodeRef!: ViewContainerRef;

  //#endregion

  //#region Properties

  protected stateComparer?: (a: any, b: any) => boolean;

  protected formattedValue = computed(() => {
    return this.valueFormatter(this.value());
  });

  protected labelFormatted = computed(() =>
    (!UTILS.OBJECT.isNil(this.value()) || this.isFocused()) &&
    !UTILS.OBJECT.isNil(this.labelFocused())
      ? this.labelFocused()
      : this.label(),
  );

  protected labelUp = computed<true | undefined>(() =>
    !UTILS.OBJECT.isNil(this.value()) || this.isFocused() ? true : undefined,
  );

  protected placeholder = computed(() =>
    (this.isFocused() && !this.value()) || this.settings().placeholderPersists
      ? this.settings()?.placeholder
      : '',
  );

  #validationResults: WritableSignal<TRegisteredValidation | undefined> =
    signal(undefined);

  override state = linkedSignal<StateEnum | undefined>(() => {
    return this.errors()?.length! > 0
      ? StateEnum.Error
      : this.warnings()?.length! > 0
        ? StateEnum.Warning
        : (this.stateService?.getState(`${this.tag()}@${this.uid()}`) ??
          StateEnum.Pristine);
  });

  errors = linkedSignal<Array<string>>(() => {
    return this.#validationResults()?.errors() ?? [];
  });
  warnings = linkedSignal<Array<string>>(() => {
    return this.#validationResults()?.warnings() ?? [];
  });

  //#endregion

  //#region Functions
  constructor() {
    super();

    //TODO: Check if this is needed
    /* afterNextRender(() => {
      if (!this.settings().stateless) {
        this.updateState();
        this.updateValidation();
      }
    }); */

    let stateFx = effect(() => {
      if (
        (this.forceValidation() || this.uid()) &&
        !this.settings().stateless
      ) {
        untracked(() => {
          this.updateState();
          this.updateValidation();
        });
      }
      if (this.settings().stateless) stateFx.destroy();
    });
  }

  protected setInitialValue(value: T | undefined) {
    if (Array.isArray(value) && value?.length === 0) {
      value = undefined;
    }
    if (
      Array.isArray(value) &&
      !UTILS.ARRAY.compareArrayByJSON(this.value() as Array<unknown>, value)
    ) {
      this.value.set(value);
    } else if (this.value() !== value) {
      this.value.set(value);
    }
    return value;
  }

  public reset() {
    this.updateValue(undefined);
  }

  public undo() {
    if (this.stateService && !this.settings().stateless) {
      let state = this.stateService.getRegisteredState(
        `${this.tag()}@${this.uid()}`,
      );
      if (state) {
        let previousValue = state.pristineValue;
        this.value.update(() => previousValue as T);
        this.updateState();
      }
    }
  }

  public updateValue(newValue: T | undefined, ...args: unknown[]) {
    if (this.permDir.uipermission() !== PERMISSIONS.VIEW) {
      if (this.value() !== newValue) {
        this.value.update(() => newValue);

        if (!this.settings().stateless) {
          this.updateState();
          this.updateValidation();
        }

        //TODO: Replace in all references with valueChange and Remove ValueChanged
        if (this.ValueChanged.observed) {
          this.ValueChanged.emit(this.value());
        }
      }
    }
  }

  private updateState() {
    if (this.stateService && !this.settings().stateless) {
      if (!this.tag()) {
        console.warn(
          'Tag or Label is required for state management on:' +
            this.settings().label,
        );
        return;
      }

      let stateExists = this.stateService.stateExists(
        `${this.tag()}@${this.uid()}`,
      );
      if (!stateExists) {
        this.stateService.registerState(
          `${this.tag()}@${this.uid()}`,
          this.value(),
          StateEnum.Pristine,
          this.stateComparer,
          this.settings().audited,
        );
      }
      this.stateService.updateState(
        `${this.tag()}@${this.uid()}`,
        this.value(),
      );
      this.state.set(this.stateService.getState(`${this.tag()}@${this.uid()}`));
    }
  }

  private updateValidation() {
    if (this.validationService && !this.settings().stateless) {
      if (!this.tag()) {
        console.warn(
          'Tag or Label is required for validation management on:' +
            this.settings().label,
        );
        return;
      }

      let validationExists = this.validationService.validationExists(
        `${this.tag()}@${this.uid()}`,
      );
      if (!validationExists) {
        this.validationService.registerValidation(
          `${this.tag()}@${this.uid()}`,
          this.value,
          this.validators(),
          this.validationChain(),
        );
      }
      this.#validationResults.set(
        this.validationService.validate(
          `${this.tag()}@${this.uid()}`,
          this.value(),
        ),
      );
      if (this.#validationResults()?.errors()?.length! > 0) {
        this.state.set(StateEnum.Error);
      } else if (this.#validationResults()?.warnings()?.length! > 0) {
        this.state.set(StateEnum.Warning);
      }
    }
  }

  protected valueFormatter(value: unknown) {
    return UTILS.OBJECT.isNil(this.value()) ? '' : this.value();
  }

  protected override focusin() {
    super.focusin();
    this.uiValueRef?.nativeElement?.focus();
  }

  protected override focusout() {
    super.focusout();
    this.uiValueRef?.nativeElement?.blur();
  }

  //#endregion
}
